(function () {
  if (customElements.get('cust-charm-selector')) return; 

  class CustCharmSelector extends HTMLElement {
    /** Wire DOM refs and event listeners when the component mounts. */
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.drawer = this.querySelector('x-drawer');
      this.drawerTitle = this.querySelector('[data-drawer-title]');
      this.confirmButton = this.querySelector('[data-confirm-order]')?.querySelector('button, a') || null;
      this.selectedTemplate = this.querySelector('[data-selected-item-template]');

      this.activeType = null;
      this.draft = {};
      this.selections = { charms: {}, birthstones: {}, chains: {} };

      this._onClick = this.#onClick.bind(this);
      this.addEventListener('click', this._onClick);

      this._onResize = () => this.#scheduleEqualize();
      window.addEventListener('resize', this._onResize);

      if (this.drawer) {
        this.drawer.addEventListener('click', this._onClick);
        this.drawer.addEventListener('dialog:after-hide', this.#onDrawerHide.bind(this));
        this.drawer.addEventListener('dialog:after-show', this.#scheduleEqualize.bind(this));
      }
    }

    /** Remove listeners when the component is removed from the page. */
    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.drawer?.removeEventListener('click', this._onClick);
      window.removeEventListener('resize', this._onResize);
      if (this._equalizeRaf) cancelAnimationFrame(this._equalizeRaf);
    }

    /** Return confirmed charm and birthstone selections for cart integration. */
    getSelections() {
      return {
        charms: this.#values(this.selections.charms),
        birthstones: this.#values(this.selections.birthstones),
        chains: this.#values(this.selections.chains),
      };
    }

    /** Restore PDP selections from cart add-on line items. */
    setSelectionsFromCart(addons) {
      const next = { charms: {}, birthstones: {}, chains: {} };
      (addons || []).forEach((item) => {
        if (!item.variantId || !item.quantity) return;
        const type = item.type === 'birthstones' ? 'birthstones' : item.type === 'chains' ? 'chains' : 'charms';
        next[type][String(item.variantId)] = { ...item };
      });
      this.selections = next;
      this.#renderSelectedList('charms');
      this.#renderSelectedList('birthstones');
      this.#renderSelectedList('chains');
    }

    /** Handle open, close, confirm, and card toggle clicks. */
    #onClick(event) {
      const open = event.target.closest('[data-open-drawer]');
      if (open) {
        event.preventDefault();
        this.#openDrawer(open.getAttribute('data-open-drawer'));
        return;
      }

      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        this.drawer?.hide?.();
        return;
      }

      if (event.target.closest('[data-confirm-order] button, [data-confirm-order] a')) {
        event.preventDefault();
        this.#confirmDrawer();
        return;
      }

      const card = event.target.closest('[data-drawer-item]');
      if (!card || !this.drawer?.contains(card) || card.getAttribute('data-available') === 'false') return;

      const variantId = card.getAttribute('data-variant-id');
      if (!variantId) return;

      event.preventDefault();
      const next = (this.draft[variantId]?.quantity || 0) > 0 ? 0 : 1;
      this.#setDraftQuantity(card, variantId, next);
      this.#syncDrawerItemUI(card, next);
      this.#updateConfirmButton();
    }

    /** Open the drawer for charms or birthstones or chains and sync draft state. */
    #openDrawer(type) {
      if (!this.drawer || !type) return;

      this.activeType = type;
      this.hadSelectionBeforeOpen = Object.keys(this.selections[type] || {}).length > 0;
      this.draft = { ...this.selections[type] };

      this.drawer.querySelectorAll('[data-drawer-panel]').forEach((panel) => {
        const active = panel.getAttribute('data-drawer-panel') === type;
        panel.hidden = !active;
        if (!active) return;

        const title = panel.getAttribute('data-drawer-title');
        if (title && this.drawerTitle) this.drawerTitle.textContent = title;

        panel.querySelectorAll('[data-drawer-item]').forEach((item) => {
          const id = item.getAttribute('data-variant-id');
          this.#syncDrawerItemUI(item, this.draft[id]?.quantity || 0);
        });
      });

      this.#updateConfirmButton();
      this.drawer.show?.();
    }

    /** Save draft to selections, update PDP list, and close the drawer. */
    #confirmDrawer() {
      if (!this.activeType) return;

      const previous = this.#values(this.selections[this.activeType]);
      const next = {};
      Object.entries(this.draft).forEach(([id, entry]) => {
        if (entry.quantity > 0) next[id] = { ...entry };
      });
      const nextValues = this.#values(next);

      this.selections[this.activeType] = next;
      this.#renderSelectedList(this.activeType);
      this.dispatchEvent(
        new CustomEvent('cust-charm-selector:change', { bubbles: true, detail: this.getSelections() }),
      );

      const toastMessage = this.#getSelectionToastMessage(previous, nextValues);
      if (toastMessage) {
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: { message: toastMessage },
          }),
        );
      }

      this.drawer?.hide?.();
    }

    /** Reset draft state and card heights when the drawer closes. */
    #onDrawerHide() {
      this.activeType = null;
      this.draft = {};
      this.hadSelectionBeforeOpen = false;
      this.#clearHeights(this.drawer, '.cust-charm-selector__drawer-item-media, .cust-charm-selector__drawer-item-card');
    }

    /** Debounce card height equalization to the next animation frames. */
    #scheduleEqualize() {
      if (this._equalizeRaf) cancelAnimationFrame(this._equalizeRaf);
      this._equalizeRaf = requestAnimationFrame(() => {
        this._equalizeRaf = requestAnimationFrame(() => {
          this._equalizeRaf = null;
          this.#equalizeDrawerCardHeights();
        });
      });
    }

    /** Clear inline heights on media or card elements. */
    #clearHeights(root, selector) {
      root?.querySelectorAll(selector).forEach((el) => {
        el.style.height = '';
      });
    }

    /** Match image and card heights within the active drawer panel. */
    #equalizeDrawerCardHeights() {
      if (!this.drawer) return;

      this.drawer.querySelectorAll('[data-drawer-panel]').forEach((panel) => {
        this.#clearHeights(panel, '.cust-charm-selector__drawer-item-media, .cust-charm-selector__drawer-item-card');
      });

      const panel = this.activeType
        ? this.drawer.querySelector(`[data-drawer-panel="${this.activeType}"]:not([hidden])`)
        : this.drawer.querySelector('[data-drawer-panel]:not([hidden])');
      if (!panel) return;

      const medias = [...panel.querySelectorAll('.cust-charm-selector__drawer-item-media')];
      const cards = [...panel.querySelectorAll('.cust-charm-selector__drawer-item-card')];
      if (!cards.length) return;

      let maxMedia = 0;
      medias.forEach((el) => {
        maxMedia = Math.max(maxMedia, el.getBoundingClientRect().height);
      });
      if (maxMedia > 0) {
        const h = `${Math.ceil(maxMedia)}px`;
        medias.forEach((el) => {
          el.style.height = h;
        });
      }

      requestAnimationFrame(() => {
        this.#clearHeights(panel, '.cust-charm-selector__drawer-item-card');
        let maxCard = 0;
        cards.forEach((el) => {
          maxCard = Math.max(maxCard, el.getBoundingClientRect().height);
        });
        if (maxCard > 0) {
          const h = `${Math.ceil(maxCard)}px`;
          cards.forEach((el) => {
            el.style.height = h;
          });
        }
      });

      panel.querySelectorAll('.cust-charm-selector__drawer-item-image').forEach((img) => {
        if (!img.complete) img.addEventListener('load', () => this.#scheduleEqualize(), { once: true });
      });
    }

    /** Store or remove one variant in the drawer draft. */
    #setDraftQuantity(item, variantId, quantity) {
      if (quantity <= 0) {
        delete this.draft[variantId];
        return;
      }
      this.draft[variantId] = {
        variantId: Number(variantId),
        productId: Number(item.getAttribute('data-product-id')),
        quantity,
        title: item.getAttribute('data-title'),
        price: Number(item.getAttribute('data-price')),
        priceFormatted: item.getAttribute('data-price-formatted'),
        image: item.getAttribute('data-image'),
        type: item.getAttribute('data-type'),
      };
    }

    /** Update badge and selected state for one drawer card. */
    #syncDrawerItemUI(item, quantity) {
      const badge = item.querySelector('[data-qty-badge]');
      if (badge) {
        const show = quantity > 0;
        badge.hidden = !show;
        badge.toggleAttribute('aria-hidden', !show);
        if (show) badge.textContent = String(quantity);
      }
      item.classList.toggle('is-selected', quantity > 0);
    }

    /** Enable confirm when draft has items or prior selections exist. */
    #updateConfirmButton() {
      if (!this.confirmButton) return;
      const canConfirm =
        Object.values(this.draft).some((e) => e.quantity > 0) || this.hadSelectionBeforeOpen;

      if (this.confirmButton.tagName === 'A') {
        this.confirmButton.setAttribute('aria-disabled', canConfirm ? 'false' : 'true');
      } else {
        this.confirmButton.disabled = !canConfirm;
      }
    }

    #getSelectionToastMessage(previous, next) {
      /* == Add-on remove success toast 2026-06-01 B == */
      const previousIds = new Set((previous || []).map((item) => String(item.variantId)));
      const nextIds = new Set((next || []).map((item) => String(item.variantId)));

      const hasAdded = [...nextIds].some((id) => !previousIds.has(id));
      const hasRemoved = [...previousIds].some((id) => !nextIds.has(id));

      if (hasAdded) return this.dataset.successMessage || 'Items added to order';
      if (hasRemoved) return this.dataset.removeSuccessMessage || 'Items removed from order';

      return '';
    }

    /** Render the PDP selected-items list for one group. */
    #renderSelectedList(type) {
      const list = this.querySelector(`[data-selected-list="${type}"]`);
      if (!list || !this.selectedTemplate) return;

      const items = this.#values(this.selections[type]);
      list.innerHTML = '';
      list.hidden = items.length === 0;
      if (list.hidden) return;

      items.forEach((entry) => {
        const node = this.selectedTemplate.content.firstElementChild.cloneNode(true);
        const img = node.querySelector('.cust-charm-selector__selected-thumb');
        const text = node.querySelector('.cust-charm-selector__selected-text');
        if (img) {
          img.src = entry.image || '';
          img.alt = entry.title || '';
        }
        if (text) {
          const price = entry.priceFormatted ? ` +${entry.priceFormatted}` : '';
          text.textContent = `${entry.quantity} x ${entry.title}${price}`;
        }
        list.appendChild(node);
      });
    }

    /** Return selection map values with quantity greater than zero. */
    #values(map) {
      return Object.values(map).filter((e) => e.quantity > 0);
    }
  }

  customElements.define('cust-charm-selector', CustCharmSelector);
})();
